/**
 * Keyboard shortcuts of the admin (T11-07, design/04 §15). Pure: `matchShortcut()` turns a key
 * press (+ the previous key for two-key sequences) into an action; the SearchProvider listens on
 * `window` and runs it. Nothing fires while the staff member types in a field or an IME (Japanese
 * input) is composing.
 *
 * | keys | action |
 * |---|---|
 * | ⌘K / Ctrl+K | open search (also inside fields) |
 * | `/` | open search |
 * | `n` | new product (on the products pages) |
 * | `g` then `o` | go to orders |
 * | `g` then `p` | go to products |
 * | `?` | list of shortcuts |
 */

export type ShortcutAction =
  "open_search" | "new_product" | "go_orders" | "go_products" | "show_shortcuts";

export interface KeyLike {
  key: string;
  metaKey?: boolean;
  ctrlKey?: boolean;
  altKey?: boolean;
  shiftKey?: boolean;
  /** `KeyboardEvent.isComposing` — true while an IME is composing. */
  isComposing?: boolean;
  /** 229 = the IME has the key (Safari reports this instead of isComposing). */
  keyCode?: number;
  target?: EventTarget | null;
}

export interface ShortcutContext {
  pathname: string;
  /** Previous plain key of a sequence (`g`) if it came within `SEQUENCE_MS`. */
  pendingPrefix: string | null;
  /** An IME composition is open (compositionstart … compositionend). */
  composing: boolean;
}

export const SEQUENCE_MS = 1200;

/** The list shown by `?` (labels are i18n keys under `shortcuts.*`). */
export const SHORTCUTS: ReadonlyArray<{ keys: readonly string[]; action: ShortcutAction }> = [
  { keys: ["⌘K", "Ctrl K"], action: "open_search" },
  { keys: ["/"], action: "open_search" },
  { keys: ["n"], action: "new_product" },
  { keys: ["g", "o"], action: "go_orders" },
  { keys: ["g", "p"], action: "go_products" },
  { keys: ["?"], action: "show_shortcuts" },
];

/** Typing into a field (input, textarea, select, contenteditable, role=textbox/combobox). */
export function isTypingTarget(target: EventTarget | null | undefined): boolean {
  if (!target || typeof (target as HTMLElement).tagName !== "string") {
    return false;
  }
  const el = target as HTMLElement;
  const tag = el.tagName.toLowerCase();
  if (tag === "textarea" || tag === "select") {
    return true;
  }
  if (tag === "input") {
    const type = ((el as HTMLInputElement).type || "text").toLowerCase();
    return !["button", "checkbox", "radio", "submit", "reset", "range", "color", "file"].includes(
      type,
    );
  }
  if (el.isContentEditable || el.getAttribute("contenteditable") === "true") {
    return true;
  }
  const role = el.getAttribute("role");
  return role === "textbox" || role === "combobox" || role === "searchbox";
}

export function isComposingKey(event: KeyLike, context: Pick<ShortcutContext, "composing">) {
  return Boolean(event.isComposing) || event.keyCode === 229 || context.composing;
}

export interface ShortcutMatch {
  action: ShortcutAction | null;
  /** Remember this key as the start of a sequence (`g`). */
  prefix: string | null;
}

const NONE: ShortcutMatch = { action: null, prefix: null };

export function matchShortcut(event: KeyLike, context: ShortcutContext): ShortcutMatch {
  if (isComposingKey(event, context)) {
    return NONE;
  }
  const key = event.key;
  // ⌘K / Ctrl+K works everywhere (also in fields — it is not a character)
  if ((event.metaKey || event.ctrlKey) && !event.altKey && key.toLowerCase() === "k") {
    return { action: "open_search", prefix: null };
  }
  if (event.metaKey || event.ctrlKey || event.altKey || isTypingTarget(event.target)) {
    return NONE;
  }
  if (context.pendingPrefix === "g") {
    if (key === "o") {
      return { action: "go_orders", prefix: null };
    }
    if (key === "p") {
      return { action: "go_products", prefix: null };
    }
  }
  switch (key) {
    case "/":
      return { action: "open_search", prefix: null };
    case "?":
      return { action: "show_shortcuts", prefix: null };
    case "g":
      return { action: null, prefix: "g" };
    case "n":
      return context.pathname === "/products" || context.pathname.startsWith("/products/")
        ? { action: "new_product", prefix: null }
        : NONE;
    default:
      return NONE;
  }
}
